import React from "react";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { AclList, Action, Resource } from "../utils/interfaces/Companies";
import { VStack } from "./utils";

interface PermissionOption {
  label: string;
  description: string;
  resource: Resource;
  action: Action;
  locked?: boolean;
}

interface PermissionGroup {
  title: string;
  options: PermissionOption[];
}

const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    title: "Employees",
    options: [
      {
        label: "View employees",
        description: "See team member list and profiles",
        resource: Resource.Employee,
        action: Action.Read,
        locked: true,
      },
    ],
  },
  {
    title: "Grants",
    options: [
      {
        label: "View grants",
        description: "See grant details and vesting for team members",
        resource: Resource.Grant,
        action: Action.Read,
        locked: true,
      },
      {
        label: "Recommend grant changes",
        description: "Submit grant state-change recommendations for approval",
        resource: Resource.Grant,
        action: Action.STATE_CHANGE,
      },
    ],
  },
  {
    title: "Reports",
    options: [
      {
        label: "Download reports",
        description: "Export team grant and vesting reports",
        resource: Resource.Document,
        action: Action.Read,
      },
    ],
  },
];

const permKey = (r: Resource, a: Action) => `${r}:${a}`;

interface Props {
  value: AclList[];
  onChange: (perms: AclList[]) => void;
}

export function ManagerPermissionPicker({ value, onChange }: Props) {
  const selected = new Set(value.map((p) => permKey(p.resource, p.action)));

  function toggle(resource: Resource, action: Action, locked: boolean) {
    if (locked) return;
    const key = permKey(resource, action);
    if (selected.has(key)) {
      onChange(value.filter((p) => permKey(p.resource, p.action) !== key));
    } else {
      onChange([...value, { resource, action }]);
    }
  }

  return (
    <VStack className="gap-4 mt-1">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
        Permissions
      </p>
      {PERMISSION_GROUPS.map((group) => (
        <div key={group.title}>
          <p className="text-sm font-semibold text-gray-700 mb-2">
            {group.title}
          </p>
          <div className="flex flex-col gap-1 pl-1">
            {group.options.map((opt) => {
              const key = permKey(opt.resource, opt.action);
              const checked = selected.has(key) || !!opt.locked;
              return (
                <FormControlLabel
                  key={key}
                  label={
                    <span className="text-sm">
                      <span className="text-gray-800">{opt.label}</span>
                      <span className="text-gray-400 ml-1 text-xs">
                        — {opt.description}
                      </span>
                    </span>
                  }
                  control={
                    <Checkbox
                      size="small"
                      checked={checked}
                      disabled={opt.locked}
                      onChange={() =>
                        toggle(opt.resource, opt.action, !!opt.locked)
                      }
                      sx={{
                        color: opt.locked ? "#E85936" : undefined,
                        "&.Mui-checked": { color: "#E85936" },
                        "&.Mui-disabled": { color: "#E85936" },
                      }}
                    />
                  }
                />
              );
            })}
          </div>
        </div>
      ))}
    </VStack>
  );
}
